import PropTypes from 'prop-types'
import { Target, Flame, CheckCircle, AlertCircle } from 'lucide-react'
import { Avatar } from '../profile'

/**
 * Compact dashboard greeting bar: avatar, name, date, projected-score + streak pills,
 * and (when a challenge is active) the status pill + Cancel/Continue controls.
 *
 * The score pill replaces the obsolete global "Level" (levels are now per challenge type,
 * shown in StrategyLevelTable). It surfaces the student's projected SAT total toward their
 * goal; if no goal/score data exists it degrades to the current estimate or a "Set a goal"
 * affordance -- never a level.
 */
const GreetingBar = ({
  user,
  todayLabel,
  currentStreak = 0,
  longestStreak = 0,
  projectedScore = null,
  currentScore = null,
  goalScore = null,
  onSetGoal,
  currentChallenge = null,
  stages = [],
  onCancelChallenge,
  onContinueChallenge,
  onStartNewChallenge
}) => {
  const allStagesCompleted =
    !!currentChallenge && stages.length > 0 && stages.every((stage) => stage.status === 'completed')

  // Prefer the forward-looking projection; fall back to the current estimate. Both are
  // combined SAT totals (400-1600).
  const displayScore = projectedScore ?? currentScore
  const isProjected = projectedScore != null

  return (
    <div className="card">
      <div className="px-4 sm:px-5 py-3 flex flex-wrap items-center gap-x-3 gap-y-2">
        <Avatar
          type={user?.avatarType}
          seed={user?.avatarSeed}
          style={user?.avatarStyle}
          name={`${user?.firstName || ''} ${user?.lastName || ''}`}
          publicName={user?.publicName}
          size="sm"
        />
        <div className="leading-tight mr-auto min-w-0">
          <p className="font-bold text-gray-900 dark:text-gray-100 truncate">
            Welcome back{user?.firstName ? `, ${user.firstName}` : ''}!
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400">{todayLabel}</p>
        </div>

        {displayScore != null ? (
          <span
            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 whitespace-nowrap"
            title={
              goalScore
                ? `${isProjected ? 'Projected' : 'Estimated'} SAT total toward your ${goalScore} goal`
                : `${isProjected ? 'Projected' : 'Estimated'} SAT total`
            }
          >
            <Target className="w-3.5 h-3.5" />
            {isProjected ? 'Projected' : 'Est.'} {displayScore}
            {goalScore ? (
              <span className="text-primary-500 dark:text-primary-400">/ {goalScore}</span>
            ) : null}
          </span>
        ) : onSetGoal ? (
          <button
            onClick={onSetGoal}
            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors whitespace-nowrap"
          >
            <Target className="w-3.5 h-3.5" />
            Set a goal
          </button>
        ) : null}
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 whitespace-nowrap">
          <Flame className="w-3.5 h-3.5 text-orange-500" />
          {currentStreak > 0
            ? `${currentStreak}-day streak`
            : longestStreak > 0
              ? `No streak · ${longestStreak} best`
              : 'No streak yet'}
        </span>

        {currentChallenge && (
          <div className="flex items-center gap-2">
            {allStagesCompleted ? (
              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-success-100 dark:bg-success-900/30 text-success-700 dark:text-success-300 whitespace-nowrap">
                <CheckCircle className="w-3.5 h-3.5" />
                Challenge complete
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-warning-100 dark:bg-warning-900/30 text-warning-700 dark:text-warning-300 whitespace-nowrap">
                <AlertCircle className="w-3.5 h-3.5" />
                Active challenge
              </span>
            )}
            <button
              onClick={onCancelChallenge}
              className="px-3 py-1.5 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors whitespace-nowrap"
            >
              {allStagesCompleted ? 'Clear' : 'Cancel'}
            </button>
            {allStagesCompleted ? (
              <button
                onClick={onStartNewChallenge}
                className="px-3 py-1.5 text-sm rounded-lg bg-success-600 hover:bg-success-700 text-white font-semibold transition-colors whitespace-nowrap"
              >
                Start New Challenge
              </button>
            ) : (
              <button
                onClick={onContinueChallenge}
                className="px-3 py-1.5 text-sm rounded-lg bg-accent-600 hover:bg-accent-700 text-white font-semibold transition-colors whitespace-nowrap"
              >
                Continue Challenge
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

GreetingBar.propTypes = {
  user: PropTypes.object,
  todayLabel: PropTypes.string.isRequired,
  currentStreak: PropTypes.number,
  longestStreak: PropTypes.number,
  projectedScore: PropTypes.number,
  currentScore: PropTypes.number,
  goalScore: PropTypes.number,
  onSetGoal: PropTypes.func,
  currentChallenge: PropTypes.object,
  stages: PropTypes.array,
  onCancelChallenge: PropTypes.func.isRequired,
  onContinueChallenge: PropTypes.func.isRequired,
  onStartNewChallenge: PropTypes.func.isRequired
}

export default GreetingBar
